import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Dh from '../components/Dh';
import { useNavigate } from 'react-router-dom';
import {
  Search, ExternalLink, Users, Rocket, Wallet,
  Calendar, ChevronDown, RotateCcw, Zap, Check,
  Briefcase, CalendarDays, Tag, Bell, UserCheck, ArrowRight, TrendingUp,
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import NewClientButton from '../components/NewClientButton';
import { can } from '@/utils/perms';
import { type Kpi } from '../components/AdminStatHero';
import { getAllUsers } from '@/utils/adminUsers';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  AreaChart, Area, XAxis, Tooltip, ResponsiveContainer,
} from 'recharts';

type SetupUser = {
  uid?: string;
  _id?: string;
  firstName: string;
  lastName: string;
  email: string;
  country?: string;
  createdAt?: string;
  businessSetupData?: {
    type?: 'incorporate' | 'operate';
    companyName?: string;
    entityType?: string;
    emirate?: string;
    industry?: string;
    planName?: string;
    planKey?: string;
    total?: number;
    submittedAt?: string;
    hasExpedited?: boolean;
    hasUpgrade?: boolean;
    businessType?: string;
    description?: string;
  };
  orderStatus?: string;
};

// Order-status pill styling (matches the workflow states on the client detail page).
const ORDER_STATUS_PILL: Record<string, { label: string; cls: string }> = {
  new:           { label: 'New',           cls: 'ax-pill ax-pill-slate' },
  in_progress:   { label: 'In progress',   cls: 'ax-pill ax-pill-brand' },
  awaiting_docs: { label: 'Awaiting docs', cls: 'ax-pill ax-pill-amber' },
  delivered:     { label: 'Delivered',     cls: 'ax-pill ax-pill-green' },
  cancelled:     { label: 'Cancelled',     cls: 'ax-pill ax-pill-red' },
};
const statusPill = (s?: string) => ORDER_STATUS_PILL[s || 'new'] || ORDER_STATUS_PILL.new;

type QuickFilter = 'incorporate' | 'operate' | 'hasExpedited' | 'hasUpgrade' | 'recentSubmissions';

const EMIRATE_OPTIONS = ['Abu Dhabi', 'Dubai', 'Sharjah', 'Ajman', 'Umm Al Quwain', 'Ras Al Khaimah', 'Fujairah'];

export default function AdminAccounting() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [allUsers, setAllUsers] = useState<SetupUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [quickFilters, setQuickFilters] = useState<QuickFilter[]>([]);

  // Advanced filter state (pending — not yet applied)
  const [filterDraft, setFilterDraft] = useState({ startDate: '', endDate: '', emirate: '', planKey: '' });
  // Applied filters
  const [filters, setFilters] = useState({ startDate: '', endDate: '', emirate: '', planKey: '' });

  useEffect(() => {
    (async () => {
      try {
        // Use the shared cached loader (stale-while-revalidate localStorage cache) so
        // /admin/accounting loads instantly on repeat visits and shares one fetch with
        // the rest of the admin — instead of re-hitting the slow /users/all every time.
        const list = (await getAllUsers()) as SetupUser[];
        // Show any user who has ANY businessSetupData (even partial — started but didn't finish),
        // excluding explicitly-consulting accounts so the corporate "orders" totals stay
        // consistent with Analytics/Admin (setup is a corporate-brand concept).
        setAllUsers(list.filter(u => (u as any).brand !== 'consulting' && u.businessSetupData != null && typeof u.businessSetupData === 'object' && Object.keys(u.businessSetupData).length > 0));
      } catch (e) {
        console.error('Error fetching users:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // --- Chart Data ---
  const chartData = useMemo(() => {
    const days: { date: string; value: number }[] = [];
    let cumulative = 0;
    const byDate: Record<string, number> = {};
    allUsers.forEach(u => {
      const raw = u.businessSetupData?.submittedAt;
      if (!raw) return;
      const key = new Date(raw).toISOString().split('T')[0];
      if (key) byDate[key] = (byDate[key] || 0) + 1;
    });
    for (let i = 29; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const key = d.toISOString().split('T')[0]!;
      cumulative += byDate[key] || 0;
      days.push({ date: key, value: cumulative });
    }
    return days;
  }, [allUsers]);

  // --- Stats ---
  const incorporateUsers = useMemo(() => allUsers.filter(u => u.businessSetupData?.type === 'incorporate'), [allUsers]);
  const operateUsers = useMemo(() => allUsers.filter(u => u.businessSetupData?.type === 'operate'), [allUsers]);
  const recentCount = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 30);
    return allUsers.filter(u => {
      const s = u.businessSetupData?.submittedAt;
      return s && new Date(s) >= cutoff;
    }).length;
  }, [allUsers]);

  // --- Filtered Users ---
  const filteredUsers = useMemo(() => {
    let list = [...allUsers];

    // Quick filters
    if (quickFilters.includes('incorporate')) list = list.filter(u => u.businessSetupData?.type === 'incorporate');
    if (quickFilters.includes('operate')) list = list.filter(u => u.businessSetupData?.type === 'operate');
    if (quickFilters.includes('hasExpedited')) list = list.filter(u => !!u.businessSetupData?.hasExpedited);
    if (quickFilters.includes('hasUpgrade')) list = list.filter(u => !!u.businessSetupData?.hasUpgrade);
    if (quickFilters.includes('recentSubmissions')) {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - 30);
      list = list.filter(u => {
        const s = u.businessSetupData?.submittedAt;
        return s && new Date(s) >= cutoff;
      });
    }

    // Advanced filters
    if (filters.startDate) {
      const start = new Date(filters.startDate);
      start.setHours(0, 0, 0, 0);
      list = list.filter(u => {
        const s = u.businessSetupData?.submittedAt;
        return s && new Date(s) >= start;
      });
    }
    if (filters.endDate) {
      const end = new Date(filters.endDate);
      end.setHours(23, 59, 59, 999);
      list = list.filter(u => {
        const s = u.businessSetupData?.submittedAt;
        return s && new Date(s) <= end;
      });
    }
    if (filters.emirate) {
      list = list.filter(u => u.businessSetupData?.emirate === filters.emirate);
    }
    if (filters.planKey) {
      list = list.filter(u => u.businessSetupData?.planKey === filters.planKey);
    }

    // Search
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(u =>
        `${u.firstName} ${u.lastName} ${u.email} ${u.businessSetupData?.companyName || ''}`.toLowerCase().includes(q)
      );
    }

    // Newest first
    list.sort((a, b) => {
      const at = a.businessSetupData?.submittedAt ? new Date(a.businessSetupData.submittedAt).getTime() : 0;
      const bt = b.businessSetupData?.submittedAt ? new Date(b.businessSetupData.submittedAt).getTime() : 0;
      return bt - at;
    });

    return list;
  }, [allUsers, quickFilters, filters, search]);

  const toggleQuickFilter = (f: QuickFilter) => {
    setQuickFilters(prev => {
      const has = prev.includes(f);
      let next = has ? prev.filter(x => x !== f) : [...prev, f];
      // incorporate / operate are mutually exclusive
      if (f === 'incorporate') next = next.filter(x => x !== 'operate');
      if (f === 'operate') next = next.filter(x => x !== 'incorporate');
      return next;
    });
  };

  const resetFilters = () => {
    setFilters({ startDate: '', endDate: '', emirate: '', planKey: '' });
    setFilterDraft({ startDate: '', endDate: '', emirate: '', planKey: '' });
    setQuickFilters([]);
  };

  const applyFilters = () => setFilters({ ...filterDraft });

  const userId = (u: SetupUser) => u._id || u.uid || '';

  const adminFirst = (() => { try { return (JSON.parse(localStorage.getItem('adminUser') || '{}').email || 'Admin').split('@')[0].replace(/^\w/, (c: string) => c.toUpperCase()); } catch { return 'Admin'; } })();
  const accKpis: Kpi[] = [
    { label: 'Total orders', value: allUsers.length, icon: <Users className="w-5 h-5" /> },
    { label: 'New this month', value: recentCount, icon: <Calendar className="w-5 h-5" /> },
    { label: 'Incorporate', value: incorporateUsers.length, icon: <Rocket className="w-5 h-5" /> },
    { label: 'Operate', value: operateUsers.length, icon: <Wallet className="w-5 h-5" /> },
  ];

  return (
    <AdminLayout
      section="Overview"
      title="Dashboard"
      icon={TrendingUp}
      hideHeader
    >
      {/* Welcome banner */}
      <div className="ax-hero">
        <div className="ax-hero-in">
          <p className="ax-eyebrow">Accounting &amp; Corporate · Admin</p>
          <h1 className="ax-hero-title">Welcome back, {adminFirst}</h1>
          <p className="ax-hero-sub">
            {`${new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} · ${allUsers.length} setup order${allUsers.length !== 1 ? 's' : ''}, ${recentCount} this month.`}
          </p>
        </div>
      </div>

        <style>{`
          /* Quick actions */
          .adminx .ax-quick { display:grid; grid-template-columns:repeat(6,1fr); gap:12px; margin-bottom:18px; }
          .adminx .ax-quick-tile { text-align:left; background:#fff; border:1px solid #E6E9ED; border-radius:14px; padding:16px; cursor:pointer; box-shadow:0 1px 2px rgba(16,24,40,.05); transition:box-shadow .15s, border-color .15s, transform .15s; font-family:inherit; }
          .adminx .ax-quick-tile:hover { box-shadow:0 10px 24px -16px var(--brand-shadow); border-color:var(--brand-line); transform:translateY(-1px); }
          .adminx .ax-quick-ic { width:40px; height:40px; border-radius:12px; display:grid; place-items:center; background:var(--brand-soft); color:var(--brand-d); margin-bottom:12px; }
          .adminx .ax-quick-ic svg { width:20px; height:20px; }
          .adminx .ax-quick-lbl { font-size:14px; font-weight:700; color:#0F172A; line-height:1.2; margin:0; }
          .adminx .ax-quick-sub { display:flex; align-items:center; gap:5px; font-size:11px; color:#94A3B8; margin:4px 0 0; }
          .adminx .ax-quick-sub svg { width:12px; height:12px; opacity:0; transform:translateX(-4px); transition:all .15s; color:var(--brand); }
          .adminx .ax-quick-tile:hover .ax-quick-sub svg { opacity:1; transform:translateX(0); }

          /* Cards */
          .adminx .ax-card { background:#fff; border:1px solid #E6E9ED; border-radius:14px; box-shadow:0 1px 2px rgba(16,24,40,.05); margin-bottom:18px; overflow:hidden; }
          .adminx .ax-card-hd { display:flex; align-items:center; justify-content:space-between; gap:12px; padding:18px 20px; border-bottom:1px solid #F1F5F9; }
          .adminx .ax-card-title { font-size:15px; font-weight:700; color:#0F172A; margin:0; }
          .adminx .ax-card-meta { font-size:12px; color:#64748B; margin:2px 0 0; }
          .adminx .ax-big { font-size:22px; font-weight:800; color:#0F172A; font-variant-numeric:tabular-nums; }
          .adminx .ax-chart { padding:14px 12px 10px; }

          /* Filters */
          .adminx .ax-filters { padding:18px 20px; }
          .adminx .ax-filter-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; margin-bottom:16px; }
          .adminx .ax-field { position:relative; }
          .adminx .ax-field > svg { position:absolute; top:50%; transform:translateY(-50%); width:16px; height:16px; color:#94A3B8; pointer-events:none; }
          .adminx .ax-field > svg.ax-lead { left:12px; }
          .adminx .ax-field > svg.ax-trail { right:12px; }
          .adminx .ax-select { width:100%; border:1px solid #E2E8F0; border-radius:10px; padding:10px 34px 10px 12px; font-size:14px; font-family:inherit; color:#0F172A; background:#FBFCFD; outline:none; appearance:none; cursor:pointer; transition:border .15s, box-shadow .15s, background .15s; }
          .adminx .ax-select:focus { border-color:var(--brand-line); background:#fff; box-shadow:0 0 0 3px rgba(240,138,36,.14); }
          .adminx .react-datepicker-wrapper, .adminx .react-datepicker__input-container { width:100%; display:block; }
          .adminx .ax-date { width:100%; border:1px solid #E2E8F0; border-radius:10px; padding:10px 12px 10px 34px; font-size:14px; font-family:inherit; color:#0F172A; background:#FBFCFD; outline:none; cursor:pointer; transition:border .15s, box-shadow .15s, background .15s; }
          .adminx .ax-date:focus { border-color:var(--brand-line); background:#fff; box-shadow:0 0 0 3px rgba(240,138,36,.14); }

          /* Quick-filter chips */
          .adminx .ax-qf-lbl { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:#94A3B8; margin:0 0 8px; }
          .adminx .ax-chips { display:flex; flex-wrap:wrap; gap:8px; align-items:center; }
          .adminx .ax-chip { display:inline-flex; align-items:center; gap:6px; border:1px solid #E2E8F0; background:#fff; color:#475569; border-radius:999px; padding:8px 15px; font-size:13px; font-weight:600; cursor:pointer; font-family:inherit; transition:all .15s; }
          .adminx .ax-chip svg { width:13px; height:13px; }
          .adminx .ax-chip:hover { background:#F8FAFC; }
          .adminx .ax-chip.is-active { background:#0F172A; border-color:#0F172A; color:#fff; }
          .adminx .ax-apply { margin-left:auto; border:none; border-radius:10px; padding:9px 18px; font-size:13px; font-weight:700; cursor:pointer; font-family:inherit; background:var(--brand); color:#fff; transition:background .15s; }
          .adminx .ax-apply:hover { background:var(--brand-d); }
          .adminx .ax-reset { border:1px solid #E2E8F0; background:#fff; color:#64748B; border-radius:10px; padding:9px; cursor:pointer; display:inline-flex; transition:all .15s; }
          .adminx .ax-reset:hover { background:#F8FAFC; color:#0F172A; }
          .adminx .ax-reset svg { width:16px; height:16px; }

          /* Table — always fits the card; columns drop instead of scrolling sideways */
          .adminx .ax-table-wrap { container-type:inline-size; overflow-x:hidden; }
          .adminx .ax-table { width:100%; table-layout:fixed; border-collapse:collapse; }
          .adminx .ax-table th { text-align:left; font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.06em; color:#94A3B8; padding:12px; background:#FBFCFD; border-bottom:1px solid #F1F5F9; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
          .adminx .ax-table td { padding:14px 12px; border-bottom:1px solid #F4F6F9; font-size:14px; vertical-align:middle; overflow-wrap:anywhere; }
          .adminx .ax-table th:first-child, .adminx .ax-table td:first-child { padding-left:20px; }
          .adminx .ax-table th:last-child, .adminx .ax-table td:last-child { padding-right:20px; text-align:right; }
          .adminx .ax-table tr:last-child td { border-bottom:none; }
          .adminx .ax-table tbody tr:hover td { background:#FAFBFC; }
          .adminx .ax-table tbody tr { cursor:pointer; }

          /* Submissions column widths (sum = 100%) */
          .adminx .ax-sub-table th:nth-child(1) { width:20%; }  /* Client    */
          .adminx .ax-sub-table th:nth-child(2) { width:10%; }  /* Type      */
          .adminx .ax-sub-table th:nth-child(3) { width:11%; }  /* Company   */
          .adminx .ax-sub-table th:nth-child(4) { width:8%;  }  /* Emirate   */
          .adminx .ax-sub-table th:nth-child(5) { width:11%; }  /* Plan      */
          .adminx .ax-sub-table th:nth-child(6) { width:9%;  }  /* Add-ons   */
          .adminx .ax-sub-table th:nth-child(7) { width:8%;  }  /* Total     */
          .adminx .ax-sub-table th:nth-child(8) { width:8%;  }  /* Submitted */
          .adminx .ax-sub-table th:nth-child(9) { width:10%; }  /* Status    */
          .adminx .ax-sub-table th:nth-child(10){ width:5%;  }  /* View      */

          /* Shed the least-important columns as the card narrows (never scroll) */
          @container (max-width:1150px) { .adminx .ax-sub-table th:nth-child(6), .adminx .ax-sub-table td:nth-child(6) { display:none; } }
          @container (max-width:980px)  { .adminx .ax-sub-table th:nth-child(4), .adminx .ax-sub-table td:nth-child(4) { display:none; } }
          @container (max-width:820px)  { .adminx .ax-sub-table th:nth-child(8), .adminx .ax-sub-table td:nth-child(8) { display:none; } }
          @container (max-width:660px)  { .adminx .ax-sub-table th:nth-child(3), .adminx .ax-sub-table td:nth-child(3) { display:none; } }
          @container (max-width:520px)  { .adminx .ax-sub-table th:nth-child(2), .adminx .ax-sub-table td:nth-child(2) { display:none; } }

          .adminx .ax-client { display:flex; align-items:center; gap:12px; min-width:0; }
          .adminx .ax-client-txt { min-width:0; flex:1; }
          .adminx .ax-avatar { width:36px; height:36px; border-radius:50%; display:grid; place-items:center; color:#fff; font-size:12px; font-weight:700; flex-shrink:0; }
          .adminx .ax-avatar.inc { background:linear-gradient(135deg,#F6B24B,var(--brand-d)); }
          .adminx .ax-avatar.op { background:linear-gradient(135deg,#F87171,#DC2626); }
          .adminx .ax-client-name { font-size:14px; font-weight:600; color:#0F172A; margin:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
          .adminx .ax-client-mail { font-size:12px; color:#94A3B8; margin:2px 0 0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
          .adminx .ax-cell-muted { color:#475569; }
          .adminx .ax-dash { color:#CBD5E1; }
          .adminx .ax-total { font-size:14px; font-weight:700; color:#0F172A; white-space:nowrap; }
          .adminx .ax-date-cell { font-size:12px; color:#64748B; white-space:nowrap; }
          .adminx .ax-addons { display:flex; gap:4px; flex-wrap:wrap; }

          /* Badges & status pills */
          .adminx .ax-badge { display:inline-flex; align-items:center; gap:5px; padding:4px 9px; border-radius:999px; font-size:11px; font-weight:700; white-space:nowrap; max-width:100%; overflow:hidden; }
          .adminx .ax-badge svg { width:12px; height:12px; flex-shrink:0; }
          .adminx .ax-badge.inc { background:var(--brand-soft); color:var(--brand-d); }
          .adminx .ax-badge.op { background:#FEE2E2; color:#DC2626; }
          .adminx .ax-badge.exp { background:#FEF3C7; color:#B45309; }
          .adminx .ax-badge.comp { background:#DCFCE7; color:#15803D; }          .adminx .ax-pill::before { content:''; width:6px; height:6px; border-radius:50%; background:currentColor; flex-shrink:0; }
          /* View button — icon-only so the last column stays narrow (whole row is clickable too) */
          .adminx .ax-view { display:inline-grid; place-items:center; width:30px; height:30px; padding:0; border:1px solid #E2E8F0; background:#fff; color:var(--brand-d); border-radius:8px; cursor:pointer; font-family:inherit; transition:all .15s; }
          .adminx .ax-view svg { width:14px; height:14px; }
          .adminx .ax-view:hover { background:#FEF6ED; border-color:var(--brand-line); }/* Loading & empty */
          .adminx .ax-spin { width:38px; height:38px; border:3px solid var(--brand); border-top-color:transparent; border-radius:50%; animation:ax-rot .7s linear infinite; margin:0 auto 14px; }
          @keyframes ax-rot { to { transform:rotate(360deg); } }
          .adminx .ax-loading p { font-size:13px; color:#94A3B8; margin:0; }
          .adminx .ax-empty { padding:64px 20px; text-align:center; }
          .adminx .ax-empty > svg { width:48px; height:48px; margin:0 auto 12px; color:#E2E8F0; }
          .adminx .ax-empty p { font-size:14px; font-weight:600; color:#64748B; margin:0 0 4px; }
          .adminx .ax-empty span { font-size:12px; color:#94A3B8; }

          @media (max-width:1100px){ .adminx .ax-quick { grid-template-columns:repeat(3,1fr); } }
          @media (max-width:900px){ .adminx .ax-stats { grid-template-columns:repeat(2,1fr); } .adminx .ax-filter-grid { grid-template-columns:repeat(2,1fr); } }
          @media (max-width:640px){ .adminx .ax-quick { grid-template-columns:repeat(2,1fr); } }
        `}</style>

        {/* Header */}
          {/* KPI stat cards */}
          <div className="stats">
            {accKpis.map(k => (
              <div key={k.label} className="stat">
                <div className="stat-ic">{k.icon}</div>
                <div className="stat-val">{k.value}</div>
                <div className="stat-lbl">{k.label}</div>
                {k.hint && <div className="stat-sub">{k.hint}</div>}
              </div>
            ))}
          </div>

          {/* Quick actions — jump to the system areas */}
          {(() => {
            const tiles = [
              { section: 'services', label: 'Services & Orders', sub: 'Fulfil setup orders', icon: Briefcase, path: '/admin/services' },
              { section: 'compliance', label: 'Compliance Calendar', sub: 'Deadlines & filings', icon: CalendarDays, path: '/admin/compliance-calendar' },
              { section: 'pricing', label: 'Accounting Pricing', sub: 'Fees, bands & packages', icon: Tag, path: '/admin/pricing' },
              { section: 'leads', label: 'Leads', sub: 'Demo & enquiries', icon: UserCheck, path: '/admin/leads' },
              { section: 'notifications', label: 'Notifications', sub: 'Broadcast & alerts', icon: Bell, path: '/admin/notifications' },
              { section: 'services', label: 'All Clients', sub: 'Full directory', icon: Users, path: '/admin/services' },
            ].filter(t => can(t.section));
            if (!tiles.length) return null;
            return (
              <div className="ax-quick">
                {tiles.map(t => (
                  <button
                    key={t.label}
                    onClick={() => navigate(t.path)}
                    className="ax-quick-tile"
                  >
                    <div className="ax-quick-ic"><t.icon /></div>
                    <p className="ax-quick-lbl">{t.label}</p>
                    <p className="ax-quick-sub">{t.sub}<ArrowRight /></p>
                  </button>
                ))}
              </div>
            );
          })()}

          {/* Submissions trend */}
          <div className="ax-card">
            <div className="ax-card-hd">
              <div>
                <h2 className="ax-card-title">Setup submissions</h2>
                <p className="ax-card-meta">Cumulative · last 30 days</p>
              </div>
              <span className="ax-big">+{recentCount}</span>
            </div>
            <div className="ax-chart">
              <div style={{ width: '100%', height: 170 }}>
                <ResponsiveContainer>
                  <AreaChart data={chartData} margin={{ top: 4, right: 6, left: 6, bottom: 0 }}>
                    <defs>
                      <linearGradient id="accGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#E8890B" stopOpacity={0.26} />
                        <stop offset="95%" stopColor="#E8890B" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis dataKey="date" hide />
                    <Tooltip
                      contentStyle={{ background: '#fff', borderRadius: 10, border: '1px solid #E2E8F0', boxShadow: '0 8px 24px -12px rgba(15,23,42,.35)', fontSize: 12 }}
                      itemStyle={{ color: '#B45309', fontWeight: 700 }}
                      labelStyle={{ color: '#64748B', marginBottom: 4 }}
                      formatter={(v: any) => [`${v}`, 'Submissions']}
                      labelFormatter={l => new Date(l).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    />
                    <Area type="monotone" dataKey="value" stroke="#E8890B" strokeWidth={2.5} fill="url(#accGrad)" fillOpacity={1} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Filters Card */}
          <div className="ax-card">
            <div className="ax-filters">
              {/* Advanced filters row */}
              <div className="ax-filter-grid">
                {/* Start Date */}
                <div className="ax-field">
                  <DatePicker
                    selected={filterDraft.startDate ? new Date(filterDraft.startDate) : null}
                    onChange={(d: Date | null) => setFilterDraft(f => ({ ...f, startDate: d ? d.toISOString().split('T')[0]! : '' }))}
                    placeholderText="Start date"
                    dateFormat="MMM d, yyyy"
                    className="ax-date"
                    isClearable
                    showMonthDropdown showYearDropdown dropdownMode="select"
                  />
                  <Calendar className="ax-lead" />
                </div>

                {/* End Date */}
                <div className="ax-field">
                  <DatePicker
                    selected={filterDraft.endDate ? new Date(filterDraft.endDate) : null}
                    onChange={(d: Date | null) => setFilterDraft(f => ({ ...f, endDate: d ? d.toISOString().split('T')[0]! : '' }))}
                    placeholderText="End date"
                    dateFormat="MMM d, yyyy"
                    className="ax-date"
                    isClearable
                    minDate={filterDraft.startDate ? new Date(filterDraft.startDate) : undefined}
                    showMonthDropdown showYearDropdown dropdownMode="select"
                  />
                  <Calendar className="ax-lead" />
                </div>

                {/* Emirate */}
                <div className="ax-field">
                  <select
                    value={filterDraft.emirate}
                    onChange={e => setFilterDraft(f => ({ ...f, emirate: e.target.value }))}
                    className="ax-select"
                  >
                    <option value="">All Emirates</option>
                    {EMIRATE_OPTIONS.map(e => <option key={e} value={e}>{e}</option>)}
                  </select>
                  <ChevronDown className="ax-trail" />
                </div>

                {/* Plan */}
                <div className="ax-field">
                  <select
                    value={filterDraft.planKey}
                    onChange={e => setFilterDraft(f => ({ ...f, planKey: e.target.value }))}
                    className="ax-select"
                  >
                    <option value="">All Plans</option>
                    <option value="starter">Starter</option>
                    <option value="professional">Professional</option>
                    <option value="enterprise">Enterprise</option>
                    <option value="pulse">Pulse</option>
                    <option value="compliance">Total Compliance</option>
                    <option value="business-in-a-box">Business-in-a-Box</option>
                  </select>
                  <ChevronDown className="ax-trail" />
                </div>
              </div>

              {/* Quick filters */}
              <div>
                <p className="ax-qf-lbl">Quick Filters</p>
                <div className="ax-chips">
                  {([
                    { key: 'incorporate', label: 'Incorporate' },
                    { key: 'operate', label: 'Operate' },
                    { key: 'recentSubmissions', label: 'Last 30 Days' },
                    { key: 'hasExpedited', label: 'Expedited', icon: <Zap className="w-3 h-3" /> },
                    { key: 'hasUpgrade', label: 'Compliance', icon: <Check className="w-3 h-3" /> },
                  ] as { key: QuickFilter; label: string; icon?: React.ReactNode }[]).map(({ key, label, icon }) => (
                    <button
                      key={key}
                      onClick={() => toggleQuickFilter(key)}
                      className={`ax-chip ${quickFilters.includes(key) ? 'is-active' : ''}`}
                    >
                      {icon}{label}
                    </button>
                  ))}
                  <button onClick={applyFilters} className="ax-apply">
                    Apply Filters
                  </button>
                  <button onClick={resetFilters} className="ax-reset" title="Reset filters">
                    <RotateCcw />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Search filters the table right below it, so it sits with it. */}
          <div className="ax-toolrow">
            <div className="ax-search">
              <Search />
              <input
                type="text"
                placeholder={t('admin.searchPlaceholder')}
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <NewClientButton />
          </div>

          {/* Table Card */}
          <div className="ax-card">
            <div className="ax-card-hd">
              <h2 className="ax-card-title">Client Submissions</h2>
              <span className="ax-card-meta">{filteredUsers.length} result{filteredUsers.length !== 1 ? 's' : ''}</span>
            </div>

            {loading ? (
              <div className="ax-loading">
                <div className="ax-spin" />
                <p>Loading clients...</p>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="ax-empty">
                <Users />
                <p>{search || quickFilters.length > 0 || filters.emirate || filters.planKey || filters.startDate || filters.endDate ? 'No results match your filters' : 'No setup clients found'}</p>
                <span>({allUsers.length} setup user{allUsers.length !== 1 ? 's' : ''} total in database)</span>
              </div>
            ) : (
              <div className="ax-table-wrap">
                <table className="ax-table ax-sub-table">
                  <thead>
                    <tr>
                      {['Client', 'Type', 'Company', 'Emirate', 'Plan', 'Add-ons', 'Total', 'Submitted', 'Status', ''].map(h => (
                        <th key={h}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(user => {
                      const uid = userId(user);
                      const isOperate = user.businessSetupData?.type === 'operate';
                      return (
                        <tr key={uid} onClick={() => uid && navigate(`/admin/user/${uid}`)}>
                          {/* Client */}
                          <td>
                            <div className="ax-client">
                              <div className={`ax-avatar ${isOperate ? 'op' : 'inc'}`}>
                                {user.firstName?.[0]}{user.lastName?.[0]}
                              </div>
                              <div className="ax-client-txt">
                                <p className="ax-client-name" title={`${user.firstName} ${user.lastName}`}>{user.firstName} {user.lastName}</p>
                                <p className="ax-client-mail" title={user.email}>{user.email}</p>
                              </div>
                            </div>
                          </td>

                          {/* Type badge */}
                          <td>
                            <span className={`ax-badge ${isOperate ? 'op' : 'inc'}`}>
                              {isOperate ? <Wallet /> : <Rocket />}
                              {isOperate ? 'Operate' : 'Incorporate'}
                            </span>
                          </td>

                          {/* Company */}
                          <td><span className="ax-cell-muted">{user.businessSetupData?.companyName || '-'}</span></td>

                          {/* Emirate */}
                          <td><span className="ax-cell-muted">{user.businessSetupData?.emirate || '-'}</span></td>

                          {/* Plan */}
                          <td><span className="ax-cell-muted">{user.businessSetupData?.planName || '-'}</span></td>

                          {/* Add-ons */}
                          <td>
                            <div className="ax-addons">
                              {user.businessSetupData?.hasExpedited && (
                                <span className="ax-badge exp"><Zap /> Expedited</span>
                              )}
                              {user.businessSetupData?.hasUpgrade && (
                                <span className="ax-badge comp"><Check /> Compliance</span>
                              )}
                              {!user.businessSetupData?.hasExpedited && !user.businessSetupData?.hasUpgrade && (
                                <span className="ax-dash">-</span>
                              )}
                            </div>
                          </td>

                          {/* Total */}
                          <td>
                            <span className="ax-total">
                              {user.businessSetupData?.total != null
                                ? <><Dh/>{user.businessSetupData.total.toLocaleString()}</>
                                : '-'}
                            </span>
                          </td>

                          {/* Submitted */}
                          <td>
                            <span className="ax-date-cell">
                              {user.businessSetupData?.submittedAt
                                ? new Date(user.businessSetupData.submittedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                                : '-'}
                            </span>
                          </td>

                          {/* Status */}
                          <td>
                            <span className={statusPill(user.orderStatus).cls}>
                              {statusPill(user.orderStatus).label}
                            </span>
                          </td>

                          {/* View */}
                          <td>
                            {uid && (
                              <button
                                onClick={e => { e.stopPropagation(); navigate(`/admin/user/${uid}`); }}
                                className="ax-view"
                                title="View client"
                                aria-label={`View ${user.firstName} ${user.lastName}`}
                              >
                                <ExternalLink />
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
    </AdminLayout>
  );
}
